import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { DownloadIcon } from "@/components/atomic/DownloadIcon";
import { Input } from "@/components/atomic/Input";
import VocsView from "@/components/ui/VocsView";
import { useDownloadSearchResult } from "@/hooks/useDownloadSearchResult";
import { useWorkspaces } from "@/hooks/useWorkspaces";
import { VocService } from "@/services/voc/voc.service";
import { useKeywordSearchStore } from "@/stores/ui/useKeywordSearchStore";
import Button from "./Button";

export const VocSearch = () => {
  const { t: tCommon } = useTranslation("common");
  const { t } = useTranslation("voiceek_ui");
  const { sessionCurrentWorkspaceId } = useWorkspaces();
  const {
    keyword,
    setKeyword,
    isLoading,
    setIsLoading,
    vocs,
    setVocs,
    total,
    setTotal,
  } = useKeywordSearchStore();
  const { handleDownload, isDownloading } = useDownloadSearchResult();

  const handleSearch = async () => {
    if (!keyword || !sessionCurrentWorkspaceId) return;

    setIsLoading(true);
    try {
      const { results, total } = await VocService.search(sessionCurrentWorkspaceId, keyword);
      setVocs(results);
      setTotal(total);
    } catch (e) {
      toast.error(tCommon("errors.unknownError"));
      console.error(e);
      setVocs([]);
      setTotal(0);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="flex-1 flex flex-col min-h-0 rounded-b-[inherit] bg-white">
      <div className="flex flex-col gap-2 p-8 min-h-0 flex-1">
        {/* 検索入力エリア */}
        <div className="max-w-[1140px] mx-auto w-full">
          <div className="flex items-center border border-[#6888E1] rounded-[5px] bg-white h-[60px] px-6 focus-within:shadow-[0_0_5px_0_#6888E1]">
            <Input
              type="text"
              placeholder={t("search.placeholder")}
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              className="flex-1 border-none h-7 shadow-none focus:ring-0 text-base placeholder:text-[#B7B7B7] px-0 caret-[#6888E1]"
              disabled={isLoading}
            />
            <Button
              variant="secondary"
              onClick={handleSearch}
              disabled={!keyword || isLoading}
              className="w-25 rounded-full shadow-[0px_2px_#3C569D]"
            >
              {t("search.button")}
            </Button>
          </div>
        </div>

        {/* ダウンロードリンク */}
        {vocs && vocs.length > 0 && (
          <div className="max-w-[1140px] mx-auto w-full flex justify-end">
            <button
              onClick={() => keyword && handleDownload(keyword)}
              disabled={isDownloading || !keyword}
              className="flex items-center gap-[6px] text-[#6888e1] hover:opacity-80 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <span className="w-[26px] h-[26px] flex items-center justify-center">
                <DownloadIcon isLoading={isDownloading} className="w-[17px] h-[17px]" />
              </span>
              <span className="font-semibold text-[14px] leading-[14px] text-nowrap whitespace-pre shrink-0">
                {t("search.download")}
              </span>
            </button>
          </div>
        )}

        {/* 検索結果 */}
        {vocs && (
          <div className="max-w-[1140px] mx-auto w-full min-h-0 flex-1">
            {vocs.length > 0
              ? (
                <VocsView
                  vocs={vocs}
                  title={total ? t("search.resultsWithCount", { count: total }) : t("search.results")}
                  innerScrollable={true}
                  className="h-full"
                />
              )
              : (
                !isLoading && (
                  <div className="text-center text-gray-500 mt-8">
                    {t("search.noResults")}
                  </div>
                )
              )}
          </div>
        )}
      </div>
    </div>
  );
};
